import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractTableDataFromElement, tableDataToCSV, tableDataToMarkdown } from 'streamdown';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, CheckOutline, DocumentDownloadOutline, DuplicateOutline, Flex, IconButton, Table, Tooltip } from '@bonusly/design-system';

import { MobileFriendlyTableWrapper } from '../../table/mobile-friendly-table';

const tableActionStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

interface BizyTableShellProps {
	/** The table body — `<Thead>`/`<Tbody>` (structured) or Streamdown children (markdown). */
	children: React.ReactNode;
	/** Base filename (no extension) for the CSV download. Defaults to "table". */
	filename?: string;
	/** Optional disclosure (e.g. the "N of M" caption) rendered between the
	 *  table and the action bar — so it sits above the actions, matching the chart. */
	caption?: React.ReactNode;
}

/**
 * The shared chrome for a Bizy table: the mobile-responsive bordered wrapper, a
 * styled `<Table>`, and a hover-revealed copy / CSV-download action bar. Copy
 * and download read the rendered DOM table via Streamdown's extractor, so this
 * works for both the markdown-table renderer (BizyMarkdownTable) and the typed
 * Node::Table renderer (BizyTable) without either reimplementing the chrome.
 */
export function BizyTableShell({ children, filename = 'table', caption }: BizyTableShellProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const tableRef = useRef<HTMLTableElement>(null);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const handleCopy = useCallback(async () => {
		const table = tableRef.current;
		if (!table) return;
		try {
			const data = extractTableDataFromElement(table);
			await navigator.clipboard.writeText(tableDataToMarkdown(data));
			setCopied(true);
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
			track(AnalyticsEvent.Bizy.TableExported, { format: 'copy', path: window.location.pathname });
		} catch {
			// Clipboard write failed silently
		}
	}, [track]);

	const handleDownload = useCallback(() => {
		const table = tableRef.current;
		if (!table) return;
		try {
			const data = extractTableDataFromElement(table);
			const csv = tableDataToCSV(data);
			const blob = new Blob([csv], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${filename}.csv`;
			a.click();
			URL.revokeObjectURL(url);
			track(AnalyticsEvent.Bizy.TableExported, { format: 'csv', path: window.location.pathname });
		} catch {
			// Download failed silently
		}
	}, [filename, track]);

	return (
		<Box sx={{ '&:hover .bizy-table-actions': { opacity: 1 } }}>
			<MobileFriendlyTableWrapper
				borderRadius="md"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				sx={{ 'tr:last-of-type td': { borderBottom: 'none' } }}
			>
				<Table ref={tableRef} variant="simple" size="sm" color="inherit">
					{children}
				</Table>
			</MobileFriendlyTableWrapper>
			{caption ? <Box mt={2}>{caption}</Box> : null}
			<Flex className="bizy-table-actions" gap="4px" mt={2} opacity={0} transition="opacity 0.15s ease-in-out">
				<Tooltip label={copied ? t('feedback.copied') : t('feedback.copyTable')} placement="top">
					<IconButton
						aria-label={t('feedback.copyTable')}
						icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
						{...tableActionStyles}
						onClick={handleCopy}
					/>
				</Tooltip>
				<Tooltip label={t('feedback.downloadCsv')} placement="top">
					<IconButton aria-label={t('feedback.downloadCsv')} icon={<DocumentDownloadOutline boxSize={5} />} {...tableActionStyles} onClick={handleDownload} />
				</Tooltip>
			</Flex>
		</Box>
	);
}
